import classnames from 'classnames';
import * as _ from 'lodash';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import type { ClipboardEvent, FocusEvent, FunctionComponent, KeyboardEvent, Ref } from 'react';

import { useInputValue } from '../../../hooks';
import { withClipboardText } from '../../../utils';
import Chip from '../Chip';

import InputBase from './InputBase';
import type { InputBaseProps, TextInputOnChange } from './InputBase';

/**
 * When a user pastes in a list of user emails from wherever source (often a CSV),
 * it could be either comma, newline or space separated. This function will split the
 * text string by all of these delimiters, returning an array of non-empty strings.
 *
 * @param {string} text - Incoming (pasted) text.
 */
function splitTextByAnyDelimiter(text: string): string[] {
  //  Find a comma, space, carriage return, or newline.
  const delimiterRegex = /[,\s\r\n]+/;
  //  If any strings are empty, we filter them out.
  return _.compact(text.split(delimiterRegex));
}

export type MultiTextInputProps = Pick<InputBaseProps, 'label' | 'name' | 'size' | 'id' | 'placeholder' | 'isRequired' | 'isDisabled' | 'testId' | 'maxLength'> & {
  /**
   * If duplicate entries should be allowed. @default false.
   */
  shouldAllowDuplicates?: boolean;
  /**
   * If duplicate entries should be determined case-insensitively.
   */
  isCaseInsensitive?: boolean;
  /**
   * maximum amount of allowed items in the multitext
   */
  maxItems?: number;
  /**
   * values to be displayed
   */
  values: string[];
  /**
   * Function to be called when change occurs
   */
  onChange?: (items: string[]) => void;
  /**
   * If you expect that the input could get quite large, you can set this to
   * ensure that the multi-text input will have a maximum height/will scroll
   */
  shouldHaveMaxHeight?: boolean;
  /**
   * Optional predicate run against each item before it is added. Items that fail
   * are not promoted to chips: a single typed entry is left in the input so the
   * user can fix it; pasted entries are dropped. Rejected items are reported via
   * `onInvalidItems`.
   */
  validate?: (item: string) => boolean;
  /**
   * Called after a commit attempt (Enter/blur/paste) with the items that failed
   * `validate`. Called with an empty array when a single entry is added
   * successfully, so consumers can clear any previously shown error.
   */
  onInvalidItems?: (items: string[]) => void;
};

type UseMultiTextInput = {
  items: string[];
  inputValue: string;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (text: string | undefined, event: ClipboardEvent<HTMLInputElement>) => void;
  getOnDeleteItem: (index: number) => () => void;
  onValueChange: TextInputOnChange;
  onBlur: (event?: FocusEvent<HTMLInputElement>) => void;
};

/**
 * Reusable hook which powers the MultiTextInput.
 * Can be used to build your own multi-text.
 */
export const useMultiTextInput = ({
  values,
  onChange = _.noop,
  shouldAllowDuplicates,
  isCaseInsensitive,
  validate,
  onInvalidItems,
}: Pick<MultiTextInputProps, 'values' | 'onChange' | 'shouldAllowDuplicates' | 'isCaseInsensitive' | 'validate' | 'onInvalidItems'>): UseMultiTextInput => {
  const [inputValue, onValueChange] = useInputValue();
  const [items, setItems] = useState<string[]>(values || []);

  //  Handle updating internal state, so this can be a controlled component.
  useEffect(
    () => {
      //  In order to prevent a runaway infinite render loop, we need to guard.
      //  This is cheap enough to do because it is just a flat array of strings.
      if (values && Array.isArray(values) && !_.isEqual(values, items)) {
        setItems(values);
      }
    },
    // This effect uses `items`, but this is a one-way sync.  If the state
    // changes we don't want to just overwrite it.  We only want this to run
    // when the props change then let the owner of the callback percolate the
    // updated value back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values],
  );

  //  Centralized logic for adding and removing items, calls onChange with the new items array.
  const onSetItems = useCallback((getNextState: (prevItems: string[]) => string[]) =>
    setItems(prevItems => {
      const nextItems = getNextState(prevItems);
      onChange(nextItems);
      return nextItems;
    }), [onChange, setItems]);

  //  Add a new item to the list
  const onAddItem = useCallback((item: string) =>
    onSetItems((prevItems => {
      if (!shouldAllowDuplicates && isCaseInsensitive) {
        // Compare new item lowercased to lowercased existing items.
        if (prevItems.map(i => i.toLowerCase()).includes(item.toLowerCase())) {
          return prevItems;
        }
      }
      if (!shouldAllowDuplicates && prevItems.includes(item)) {
        return prevItems;
      }
      return [...prevItems, item];
    })), [shouldAllowDuplicates, onSetItems, isCaseInsensitive]);

  const getOnDeleteItem = useCallback((index: number) => () =>
    onSetItems(prevItems => prevItems.slice(0, index).concat(prevItems.slice(index + 1))), [onSetItems]);

  //  Promote the current input to a chip. When a `validate` predicate is supplied
  //  and rejects the entry, leave it in the input (so the user can fix it) and
  //  report it via `onInvalidItems` instead of adding it.
  const commitInputValue = useCallback(() => {
    const candidate = inputValue.trim();
    //  If the input is empty, don't add anything — but clear any outstanding
    //  invalid-items error, so the user can dismiss it by emptying the input.
    if (candidate.length === 0) {
      onInvalidItems?.([]);
      return;
    }
    if (validate && !validate(candidate)) {
      onInvalidItems?.([candidate]);
      return;
    }
    onInvalidItems?.([]);
    onAddItem(validate ? candidate : inputValue);
    onValueChange('');
  }, [inputValue, validate, onInvalidItems, onAddItem, onValueChange]);

  const handleEnterPress = commitInputValue;

  const handleBackspacePress = useCallback(() => {
    //  If all other characters have been removed, delete the last item in items.
    if (inputValue.length === 0) {
      onSetItems(prevItems => prevItems.slice(0, -1));
    }
  }, [inputValue, onSetItems]);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleEnterPress();
    } else if (event.key === 'Backspace') {
      handleBackspacePress();
    }
  }, [handleEnterPress, handleBackspacePress]);

  //  If the user pastes comma-separated values, insert them as new items.
  const onPaste = useCallback((text: string | undefined, event: ClipboardEvent<HTMLInputElement>) => {
    if (!text) {
      return;
    }

    //  Prevents the pasted text from actually being added to the input.
    //  We want this because the onChange event happens AFTER the onPaste,
    //  and we're just going to clear the input after adding the items anyway.
    event.preventDefault();

    //  We call onAddItem for each item, so that we can handle duplicates and case-insensitivity.
    //  Items failing `validate` are dropped (they can't all sit in the single input) and reported.
    const pastedItems = splitTextByAnyDelimiter(text);
    if (validate) {
      const [valid, invalid] = _.partition(pastedItems, item => validate(item));
      onInvalidItems?.(invalid);
      valid.forEach(item => onAddItem(item));
    } else {
      pastedItems.forEach(item => onAddItem(item));
    }
  }, [onAddItem, validate, onInvalidItems]);

  //  If there is anything inside the input when the input loses focus, just add it to the list for them.
  const onBlur = commitInputValue;

  return {
    items,
    inputValue,
    onKeyDown,
    onPaste,
    getOnDeleteItem,
    onValueChange,
    onBlur,
  };
};

const defaultValues: string[] = [];

/**
 * MultiTextInput is a text input which allows multiple items to be added.
 */
const MultiTextInput: FunctionComponent<MultiTextInputProps> = ({
  label,
  name,
  size,
  id,
  placeholder,
  values = defaultValues,
  isRequired,
  isDisabled,
  testId,
  maxLength,
  maxItems,
  onChange = _.noop,
  shouldAllowDuplicates = false,
  isCaseInsensitive = false,
  shouldHaveMaxHeight = false,
  validate,
  onInvalidItems,
}, ref: Ref<HTMLInputElement>) => {
  const { items, inputValue, onKeyDown, onValueChange, getOnDeleteItem, onPaste, onBlur } = useMultiTextInput({
    values,
    onChange,
    shouldAllowDuplicates,
    isCaseInsensitive,
    validate,
    onInvalidItems,
  });
  return (
    <InputBase
      label={label}
      size={size}
      id={id}
      placeholder={placeholder}
      isRequired={isRequired}
      isDisabled={isDisabled}
      testId={testId}
      maxLength={maxLength}
      name={name}
    >
      {({ className, ...inputProps }) => (
        <div
          className={classnames(className, 'fossa-form__multi-text-input', {
            'fossa-form__multi-text-input--max-height': shouldHaveMaxHeight,
          })}
        >
          {items?.map((item, index) => (
            <Chip
              key={item}
              text={item}
              size={size}
              isDisabled={isDisabled}
              onDelete={getOnDeleteItem(index)}
            />
          ))}
          { ((maxItems === undefined) || (values.length < maxItems)) && (
            <input
              //  Note that we are intentionally spreading here and overriding below.
              {...inputProps} // eslint-disable-line react/jsx-props-no-spreading
              ref={ref}
              type="text"
              disabled={isDisabled}
              className="fossa-form__multi-text-input__input"
              onKeyDown={onKeyDown}
              onChange={event => onValueChange(event.target.value)}
              onPaste={withClipboardText(onPaste)}
              onBlur={onBlur}
              value={inputValue}
            />
          )}
        </div>
      )}
    </InputBase>
  );
};

export default forwardRef<HTMLInputElement, MultiTextInputProps>(MultiTextInput);
